import { useNavigate } from "react-router-dom";
import { useNavigationGuard } from "@/lib/navigation/navigationGuard";

type BackButtonProps = {
  label: string;
  fallbackHref?: string;
  /** Hide on desktop (lg+). Use for pages that are already reachable via the sidebar. */
  hideOnDesktop?: boolean;
};

export function BackButton({ label, hideOnDesktop }: BackButtonProps) {
  const navigate = useNavigate();
  const guard = useNavigationGuard();

  const navigateBack = () => {
    navigate(-1);
  };

  const handleClick = () => {
    if (guard?.isDirty) {
      guard.promptDiscard(() => navigateBack());
      return;
    }
    navigateBack();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={label}
      className={[
        "flex items-center gap-1.5 rounded-lg text-sm font-semibold",
        "text-[color:var(--text-muted)] transition-colors",
        // Mobile: comfortable tap target with label
        "p-1.5",
        // Desktop: tighter icon-only button with subtle hover bg
        "lg:p-2 lg:hover:bg-[color:var(--background-muted)]",
        hideOnDesktop ? "lg:hidden" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Arrow points left in LTR; rtl:rotate-180 flips it to point right in RTL */}
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 rtl:rotate-180"
        aria-hidden="true"
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
      {/* Label visible on mobile only */}
      <span className="lg:hidden">{label}</span>
    </button>
  );
}
